import { useEffect, useState, createContext, useContext, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SEOContext = createContext({ overrides: {}, publishedArticles: [], refresh: () => {} });

export function SEOProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [publishedArticles, setPublishedArticles] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [overridesData, articlesData] = await Promise.all([
        fetch(`${BACKEND_URL}/api/seo/overrides`).then((r) => (r.ok ? r.json() : {})),
        fetch(`${BACKEND_URL}/api/articles/published`)
          .then((r) => (r.ok ? r.json() : { items: [] }))
          .catch(() => ({ items: [] })),
      ]);
      setOverrides(overridesData || {});
      setPublishedArticles(articlesData?.items || []);
    } catch {
      // Silently fail — pages render with code defaults
      setOverrides({});
      setPublishedArticles([]);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return <SEOContext.Provider value={{ overrides, publishedArticles, refresh }}>{children}</SEOContext.Provider>;
}

// Normalise: strip trailing slash except for root
function normalisePath(p) {
  if (!p || p === "/") return "/";
  return p.replace(/\/+$/, "") || "/";
}

/**
 * Returns the override object for a given path (empty object if none set).
 * Use to merge over code-default meta/H1/intro values.
 */
export function useSEOOverride(path) {
  const { overrides } = useContext(SEOContext);
  const norm = normalisePath(path || "/");
  return overrides[norm] || {};
}

// Direct accessor for the full overrides map (used by admin page).
export function useAllSEOOverrides() {
  return useContext(SEOContext);
}

// Returns the CMS-published article list (for /resources + Article.jsx merging).
export function usePublishedArticles() {
  const { publishedArticles } = useContext(SEOContext);
  return publishedArticles || [];
}
